import { useCallback, useMemo } from 'react';
import { useInteractiveModeSession } from 'copilot-toolkit/hooks/useInteractiveModeSession';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { toPlaylistItemType } from '../../playlistItems/playlistItemsTypes.migrated';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import UsageTracker from '../../../utils/UsageTracker';
export const usePlaylistBreezeSession = (playlistItems, saveNewPlaylistItems, mode = 'create') => {
  const addItemsToPlaylist = useCallback(async ({
    itemsToAdd
  }) => {
    try {
      const toAdd = itemsToAdd.map(item => {
        const type = toPlaylistItemType(item.type);
        if (!type) return null;
        return {
          itemId: item.id,
          type,
          language: convertToSupportedLanguage(item.language),
          visibility: item.visibility,
          name: item.title,
          hasBeenRemoved: false
        };
      }).filter(item => item !== null);
      const existingItems = playlistItems.map(item => Object.assign({}, item, {
        hasBeenRemoved: false
      }));
      const newItems = toAdd.filter(item => !existingItems.some(existingItem => existingItem.itemId === item.itemId));
      await saveNewPlaylistItems([...existingItems, ...newItems]);
      UsageTracker.trackStandalone('Playlist Interaction', {
        action: 'add-items-to-playlist-breeze',
        count: itemsToAdd.length
      });
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.itemsAddedToPlaylist', {
          count: itemsToAdd.length
        }),
        type: 'success'
      });
    } catch (error) {
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.failedToAddItems'),
        type: 'danger'
      });
    }
  }, [playlistItems, saveNewPlaylistItems]);
  const removeItemsFromPlaylist = useCallback(async ({
    itemIdsToRemove
  }) => {
    try {
      const idsToRemove = new Set(itemIdsToRemove.map(id => id));
      const remainingItems = playlistItems.filter(item => !idsToRemove.has(item.itemId)).map(item => Object.assign({}, item, {
        hasBeenRemoved: false
      }));
      await saveNewPlaylistItems(remainingItems);
      UsageTracker.trackStandalone('Playlist Interaction', {
        action: 'remove-items-from-playlist-breeze',
        count: itemIdsToRemove.length
      });
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.itemsRemovedFromPlaylist', {
          count: itemIdsToRemove.length
        }),
        type: 'success'
      });
    } catch (error) {
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.failedToRemoveItems'),
        type: 'danger'
      });
    }
  }, [playlistItems, saveNewPlaylistItems]);
  const replacePlaylistItems = useCallback(async ({
    items
  }) => {
    try {
      const newItems = items.map(item => {
        const type = toPlaylistItemType(item.type);
        if (!type) return null;
        return {
          itemId: item.id,
          type,
          language: convertToSupportedLanguage(item.language),
          visibility: item.visibility,
          name: item.title,
          hasBeenRemoved: false
        };
      }).filter(item => item !== null);
      await saveNewPlaylistItems(newItems);
      UsageTracker.trackStandalone('Playlist Interaction', {
        action: 'replace-playlist-items-breeze',
        count: items.length
      });
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.playlistReplaced', {
          count: items.length
        }),
        type: 'success'
      });
    } catch (error) {
      FloatingAlertStore.addAlert({
        message: I18n.text('learningCenter.myPlaylists.failedToReplacePlaylist'),
        type: 'danger'
      });
    }
  }, [saveNewPlaylistItems]);
  const activePlaylistItems = useMemo(() => playlistItems.filter(i => !i.hasBeenRemoved), [playlistItems]);
  const additionalLLMInstructions = useMemo(() => {
    const playlistJson = JSON.stringify(activePlaylistItems.map(i => ({
      id: String(i.itemId),
      name: i.name,
      type: i.type
    })));
    return `
      ## Mode: ${mode.toUpperCase()}
      ${mode === 'edit' ? 'User is EDITING an existing playlist. Offer both "Add to Playlist" and "Replace Playlist" options.' : 'User is CREATING a new playlist. Just add items directly.'}

      ## How This Works
      Actions require user confirmation via a button. Your response appears BEFORE the button, so never say "added" or "removed" - it hasn't happened yet.

      ## Current Playlist (${activePlaylistItems.length} items)
      ${playlistJson}

      ## Searching

      When searching for content, you can only search for the following content types: Course, Lesson, Video. On the backend, these are mapped to the following content types: TRACK, LESSON, SHORT_FORM_CONTENT.

      IMPORTANT: When answering always use Course, Lesson, Video when referring to the content types. Do not use the internal enum names.

      If a user asks for short content, demos, or tutorials search for "SHORT_FORM_CONTENT" content type.

      ## Response Format (ALWAYS follow this format)
      When presenting search results, ALWAYS use this exact format:

      Here's what I found:
      1. Item Name (Lesson)
      2. Item Name (Course)
      3. Item Name (Lesson)
      4. Item Name (Video)


      Which would you like to add? You can say "add 1 and 3", "add all", or pick specific numbers.

      ## Adding Content
      1. Search with ContentSearchTools
      2. Present as numbered list (format above)
      3. Wait for user to pick numbers (e.g., "1 and 3", "all", "1, 2, 4")
      4. ${mode === 'edit' ? `IMPORTANT: In edit mode, you MUST call BOTH actions simultaneously to give the user a choice:
         - Call addItemsToPlaylist (adds to existing playlist)
         - Call replacePlaylistItems (replaces entire playlist)
         Both buttons should appear so the user can choose. Say "Add to playlist or replace it?" before the buttons.` : 'Call addItemsToPlaylist with selected items'}

      ## Removing Content
      1. Show numbered list of current items
      2. Wait for user to pick which to remove
      3. Call removeItemsFromPlaylist with selected IDs

      ## Rules
      - Be concise. No verbose explanations.
      - Always use numbered lists
      - Never show IDs to user
      - Content types in Title case: Lesson, Course, Certification, Video
      - Respond in ${getLang()} only
      - When calling actions, do NOT add titles or descriptions around them. Just call the actions - the buttons speak for themselves.
    `;
  }, [activePlaylistItems, mode]);
  const itemDescription = {
    type: 'object',
    description: 'Content item with ID, type, title, and language',
    properties: {
      id: {
        type: 'number',
        description: 'Content item ID'
      },
      type: {
        type: 'string',
        description: 'LESSON, TRACK, or SHORT_FORM_CONTENT'
      },
      title: {
        type: 'string',
        description: 'Content item title'
      },
      language: {
        type: 'string',
        description: 'Content language code (e.g., EN, ES, DE, FR, PT, JA)'
      },
      visibility: {
        type: 'string',
        description: 'Content visibility'
      }
    }
  };
  const {
    sendMessageFromAppToCopilot,
    isCopilotReady,
    openInteractiveMode
  } = useInteractiveModeSession({
    sessionId: `playlist-builder-session-${mode}`,
    appName: 'LearningCenterUI',
    defaultToolSet: 'com.hubspot.learning.center.playlists.ai.rpc.ContentSearchTools',
    interactiveModeActions: [{
      name: 'addItemsToPlaylist',
      function: addItemsToPlaylist,
      functionDescription: 'Add selected content items to the current playlist.',
      autoApplySetting: 'never-auto-apply',
      undoSetting: 'undo-and-reapply-disabled',
      functionParameters: {
        itemsToAdd: {
          type: 'array',
          description: 'Array of content items to add',
          items: itemDescription
        }
      }
    }, {
      name: 'removeItemsFromPlaylist',
      function: removeItemsFromPlaylist,
      functionDescription: 'Remove items from the current playlist by their IDs.',
      autoApplySetting: 'never-auto-apply',
      undoSetting: 'undo-and-reapply-disabled',
      functionParameters: {
        itemIdsToRemove: {
          type: 'array',
          description: 'Array of item IDs to remove',
          items: {
            type: 'number',
            description: 'Item ID'
          }
        }
      }
    }, {
      name: 'replacePlaylistItems',
      function: replacePlaylistItems,
      functionDescription: 'Replace all playlist items with new items (removes existing items).',
      autoApplySetting: 'never-auto-apply',
      undoSetting: 'undo-and-reapply-disabled',
      functionParameters: {
        items: {
          type: 'array',
          description: 'Array of content items to set as the new playlist',
          items: itemDescription
        }
      }
    }],
    additionalLLMInstructions
  });
  return {
    sendMessageFromAppToCopilot,
    isCopilotReady,
    openInteractiveMode
  };
};